import { FC, useState } from 'react';
import { Box, Button, Dialog, Hidden, Icon, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import FileViewer from 'components/DocumentViewer';
import Zoom from 'components/DocumentViewer/Zoom';
import { useGoogleTagManager } from 'use/gtm';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from 'components/LoadingIndicator';
import { useManual } from './useManual';
import { SlideUpTransition } from '../../lib/Transition/transition';

const useStyles = makeStyles((theme) => ({
  main: {
    flexGrow: 100,
  },
  fullHeight: {
    height: '100%',
    flexGrow: 100,
  },
  header: {
    position: 'sticky',
    top: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'stretch',
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    width: '100%',
    minHeight: 72,
    flexGrow: 1,
    boxShadow: theme.shadows[6],
    zIndex: 3,
  },
  headerLeft: {
    flexGrow: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  headerCenter: {
    flexGrow: 2,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    margin: theme.spacing(1),
    color: theme.palette.grey[600],
    fontWeight: 'bold',
  },
  headerRight: {
    flexGrow: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    color: theme.palette.grey[600],
    fontWeight: 'bold',
  },
  headerIcon: {
    'margin': 0,
    'color': theme.palette.grey[600],
    '&:hover': {
      color: theme.palette.grey[900],
    },
    [theme.breakpoints.up('sm')]: {
      margin: theme.spacing(0, 1, 0, 1),
    },
  },
  btnIcon: {
    transform: 'scale(0.8)',
  },
  closeIcon: {
    width: 24,
    height: 24,
  },
  closeButton: {
    'margin': theme.spacing(1, 3, 1, 1),
    'float': 'right',
    'backgroundColor': 'transparent',
    'borderColor': 'transparent',
    'color': theme.palette.grey[600],
    '&:hover': {
      color: theme.palette.grey[900],
      cursor: 'pointer',
    },
  },
  documentIcon: {
    width: 24,
    height: 24,
    margin: theme.spacing(1, 1, 1, 3),
    color: theme.palette.grey[600],
  },
  button: {
    display: 'flex',
    alignItems: 'center',
  },
  actionBtn: {
    'color': theme.palette.grey[600],
    'paddingRight': theme.spacing(2),
    '&:hover': {
      color: theme.palette.grey[900],
    },
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
};

const ViewManualDocument: FC<Props> = ({ open, onClose }) => {
  const reset = () => {};
  const [scale, setScale] = useState(1);
  const googleTagManager = useGoogleTagManager();
  const { t } = useTranslation();
  const manual = useManual();

  const handleCancel = () => {
    reset();
    onClose();
    googleTagManager({ event: 'ButtonClick', btnName: 'ExitManual' });
  };

  const handleZoom = (scalePercentage: number) => {
    setScale(scalePercentage / 100);
    googleTagManager({ event: 'Zoom' });
  };

  const fileDownload = require('js-file-download');
  const handleDownload = () => {
    fileDownload(manual.data, manual.data?.name);
    googleTagManager({ event: 'ButtonClick', btnName: 'DownloadManualFile' });
  };

  if (open && manual.isIdle) {
    manual.refetch();
  }

  const classes = useStyles();

  return (
    <Dialog
      fullScreen
      open={open}
      TransitionComponent={SlideUpTransition}
      onClose={onClose}
      TransitionProps={{ onExited: reset }}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="stretch"
        className={classes.fullHeight}
      >
        <ViewManualDocumentHeader
          fileInfo={manual.data}
          status={manual.status}
          onZoom={handleZoom}
          onDownloadPdf={handleDownload}
          onClose={handleCancel}
        />

        <div className={classes.main}>
          {manual.status === 'loading' && <LoadingIndicator />}
          {manual.status === 'success' && <FileViewer file={manual.data} scale={scale} />}
          {manual.status === 'error' && (
            <Box height="50%" display="flex" justifyContent="center" alignItems="center">
              <Typography variant="h6">{t('error.default')}</Typography>
            </Box>
          )}
        </div>
      </Box>
    </Dialog>
  );
};

type HeaderProps = {
  fileInfo?: File;
  status: string;
  onZoom: (scale: number) => void;
  onClose: () => void;
  onDownloadPdf: () => void;
};

const ViewManualDocumentHeader: FC<HeaderProps> = ({
  fileInfo,
  status,
  onZoom,
  onDownloadPdf,

  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <div className={classes.header}>
      <div className={classes.headerLeft}>
        <div className={classes.headerIcon}>
          <Icon className={classes.documentIcon}>file_text</Icon>
        </div>
        <Typography variant="h6">{fileInfo?.name}</Typography>
      </div>

      <div className={classes.headerCenter}>
        <Zoom onZoom={onZoom} />
      </div>

      <div className={classes.headerRight}>
        <div className={classes.button}>
          <Button
            disabled={status !== 'success'}
            onClick={onDownloadPdf}
            variant="text"
            className={classes.actionBtn}
            type="submit"
            startIcon={<Icon className={classes.btnIcon}>download</Icon>}
          >
            <Hidden smDown>{t('manual.viewer.download')}</Hidden>
          </Button>
        </div>
        <button type="button" onClick={onClose} className={classes.closeButton}>
          <Icon className={classes.closeIcon}>close</Icon>
        </button>
      </div>
    </div>
  );
};

export default ViewManualDocument;
